import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box, Button, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ControlledTextField, ControlledSelect } from '@/components/inputs'
import { AttentionTaskType, FormComponentProps } from '@/types'

export type AttentionTaskFormData = {
  type: AttentionTaskType
  message: string
}

export type AttentionTaskFormProps = FormComponentProps<AttentionTaskFormData>

export const AttentionTaskForm = ({
  onSubmit,
  defaultValues,
  isLoading,
}: AttentionTaskFormProps): JSX.Element => {
  const { t } = useTranslation('forms')

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
  } = useForm<AttentionTaskFormData>({
    mode: 'onChange',
    defaultValues: {
      type: undefined,
      message: '',
    },
    values: defaultValues,
  })

  const handleReset = () =>
    reset({
      ...defaultValues,
    })

  const attentionTaskTypeOptions = [
    'photo_task',
    'sound_record_task',
    'fieldreport_task',
    'edit_position_of_observation',
    'baltsverblijf_without_territorium',
    'missing_batlogger_data',
    'other_task',
  ].map((value) => ({ name: t(`attentionTaskTypes.${value}`), value }))

  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        await onSubmit(data)
        reset({ type: 'photo_task', message: '' })
      })}
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          {t('descriptions.createAttentionTask')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={5} my={2}>
          <Stack width={550} spacing={4}>
            <Box>
              <ControlledSelect
                name="type"
                control={control}
                options={attentionTaskTypeOptions}
                placeholder={t('placeholders.attentionTaskType')}
                displayEmpty={true}
                rules={{ required: 'required' }}
              />
            </Box>
            <Box>
              <ControlledTextField
                name="message"
                control={control}
                placeholder={t('placeholders.attentionTaskMessage')}
                multiline={true}
                // rules={{ required: true }}
                inputProps={{
                  style: {
                    padding: 0,
                  },
                }}
              />
            </Box>
          </Stack>
          {isDirty && (
            <Stack direction="row" spacing={5} pt={1}>
              <Button
                variant="textThin"
                color="secondary"
                onClick={handleReset}
              >
                {t('buttons.cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                color="secondary"
                type="submit"
                loading={isLoading}
                disableElevation={true}
                disabled={!isDirty || !isValid}
              >
                <span>{t('buttons.create')}</span>
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Stack>
    </form>
  )
}
